import React, { useCallback, useEffect, useState } from 'react';
import TransactionStatusTab, {
  TransactionStatusTabListType,
  TransactionStatusTabType
} from './status-tab';
import { Paginate, Table, TableData, TableRow } from '@cokitchen/cokitchen-components';
import moment from 'moment';
import { debounce, formatCurrency, handleApiError } from 'utils/functions';
import { apiService } from 'services/base.service';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { useParams, Link } from 'react-router-dom';
import { PaymentRequestDetailType } from 'models/payment-tracker.model';

const TABLE_HEADERS = [
  'Code',
  'Item',
  'Requested',
  'Received',
  'Amount',
  'Date Created',
  'Due Date',
  'Status',
  'Action',
  ''
];

const TAB_LIST: TransactionStatusTabListType[] = [
  {
    text: 'All',
    value: 'all'
  },
  {
    text: `Requested`,
    value: 'REQUESTED'
  },
  {
    text: `Approved`,
    value: 'APPROVED'
  },
  {
    text: `Paid`,
    value: 'PAID'
  }
];

const TransactionHistory = () => {
  const [activeTab, setActiveTab] = useState<TransactionStatusTabType>('all');
  const [isFetchingTransactionHistory, setIsFetchingTransactionHistory] = useState(false);
  const [vendorsTransactions, setVendorsTransactions] = useState<PaymentRequestDetailType[]>([]);
  const { id } = useParams();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [lastPage, setLastPage] = useState<number>(1);

  const fetchTransactionHistory = async (
    current_page: number,
    active_tab: TransactionStatusTabType
  ) => {
    try {
      setIsFetchingTransactionHistory(true);
      const params: any = {
        vendor_id: id,
        page: current_page
      };

      if (active_tab !== 'all') {
        params.status = active_tab;
      }

      const response = await apiService.get('payment-request', {
        version: 'v2',
        params
      });
      if (response) {
        setVendorsTransactions(response.data.data);
        setLastPage(
          Math.ceil(response.data.pagination.total / response.data.pagination.per_page || 1)
        );
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsFetchingTransactionHistory(false);
    }
  };

  const debounceFetchTransactionHistory = useCallback(debounce(fetchTransactionHistory, 1000), []);

  useEffect(() => {
    setVendorsTransactions([]);
    setIsFetchingTransactionHistory(true);
    debounceFetchTransactionHistory(currentPage, activeTab);
  }, [currentPage, activeTab]);

  return (
    <div className='bg-[#F7F7F8] w-full h-[100%] p-1 rounded-md '>
      <div className='bg-white w-full rounded-t-md pt-4 py-3 flex items-center justify-between mb-1 px-6'>
        <label className='text-base md:text-lg font-medium'>Transaction history</label>
        <TransactionStatusTab active={activeTab} setActive={setActiveTab} list={TAB_LIST} />
      </div>

      <div className='mt-6'>
        {isFetchingTransactionHistory ? (
          <div className='text-xl leading-[40vh] text-center'>Loading...</div>
        ) : vendorsTransactions?.length === 0 ? (
          <div className='text-xl leading-[40vh] text-center'>No Transactions</div>
        ) : (
          <div className='no-scroll-bar whitespace-nowrap pb-10 px-4'>
            <Table headers={TABLE_HEADERS}>
              {vendorsTransactions?.map((item, idx) => (
                <TableRow className='text-gray-9 font-medium text-sm' key={idx}>
                  <TableData>#{String(item.code)}</TableData>
                  <TableData>{item.request_item?.ingredient?.name}</TableData>
                  <TableData>
                    {String(item.request_item.quantity)}{' '}
                    {item.request_item?.ingredient?.unit_type ?? 'pcs'}
                  </TableData>
                  <TableData>
                    {String(item.request_item.quantity_received)}{' '}
                    {item.request_item?.ingredient?.unit_type ?? 'pcs'}
                  </TableData>
                  <TableData>{formatCurrency(item.amount)}</TableData>
                  <TableData>{moment(item.created_at).format('Do MMM, YYYY')}</TableData>
                  <TableData>{moment(item.payment_due_date).format('Do MMM, YYYY')}</TableData>
                  <TableData
                    className={`${
                      item.status === 'PAID'
                        ? 'text-[#3FB827]'
                        : item.status === 'APPROVED'
                        ? 'text-[#0066FF]'
                        : item.status === 'REQUESTED'
                        ? 'text-[#FF9500]'
                        : 'text-[#FF0000]'
                    }`}>
                    <div className='flex items-center capitalize gap-2'>
                      <p
                        className={`rounded-full w-[7px] h-[7px] ${
                          item.status === 'PAID'
                            ? 'bg-[#3FB827]'
                            : item.status === 'APPROVED'
                            ? 'bg-[#0066FF]'
                            : item.status === 'REQUESTED'
                            ? 'bg-[#FF9500]'
                            : 'bg-[#FF0000]'
                        }`}
                      />
                      {item.status}
                    </div>
                  </TableData>
                  <TableData>
                    <Link
                      to={`${MAIN_STORE_ROUTE_KEYS.PAYMNET_TRACKER}/update/${item.id}`}
                      className='text-red-5 hover:text-red-6 cursor-pointer underline '>
                      View Details
                    </Link>
                  </TableData>
                  <TableData>
                    <>
                      {item.receipt_url ? (
                        <button
                          className='text-red-5 underline underline-offset-2 font-medium cursor-pointer'
                          onClick={() => window.open(item.receipt_url || '')}>
                          View receipt
                        </button>
                      ) : null}
                    </>
                  </TableData>
                </TableRow>
              ))}
            </Table>

            <div className='flex justify-end'>
              <Paginate
                pageCount={lastPage}
                onPageChange={(value) => setCurrentPage(value.selected + 1)}
                pageRangeDisplayed={2}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionHistory;
//
